import { useEffect, useState } from 'react';
import DocumentAnnotator from './document-annotator/document-annotator';
import DocumentUpload from './DocumentUpload';
import styles from './sign-document.module.scss';
import { hasSeenTour, startTour } from '@/hooks/use-onboarding-tour';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';

export type FileToUpload = {
	lastModified: number;
	name: string;
	size: number;
	type: string;
	webkitRelativePath: string;
};

//TODO: rename this to DocumentTemplateBuilder
const UPLOAD_TOUR_STEPS = [
	{
		element: '[data-tour="upload-dropzone"]',
		popover: {
			title: 'Sube el documento a firmar',
			description:
				'El primer paso es subir tu documento en formato PDF. Arrastra o haz click en esta sección para exportar tu documento.',
		},
	},
];

const ANNOTATOR_TOUR_STEPS_DESKTOP = [
	{
		element: '[data-tour="annotation-canvas"]',
		popover: {
			title: 'Visualizador del documento',
			description:
				'Aquí puedes visualizar el documento subido y colocar la firma en la página que desees.',
		},
	},
	{
		element: '[data-tour="annotation-sidebar"]',
		popover: {
			title: 'Barra de herramientas',
			description:
				'Haz click o arrastra esta opción para colocar la firma en la sección del documento que la desees.',
			side: 'left' as const,
		},
	},
	{
		element: '[data-tour="annotation-send-btn"]',
		popover: {
			title: 'Envía a firmar el documento',
			description:
				'Cuando hayas colocado en el documento los campos a firmar, haz click aquí para enviar a firmar el documento.',
			side: 'bottom' as const,
		},
	},
];

const ANNOTATOR_TOUR_STEPS_MOBILE = [
	{
		element: '[data-tour="annotation-canvas"]',
		popover: {
			title: 'Visualizador del documento',
			description:
				'Aquí puedes visualizar el documento subido y colocar la firma en la página que desees.',
		},
	},
	{
		element: '[data-tour="annotation-sidebar"]',
		popover: {
			title: 'Barra de herramientas',
			description:
				'Haz click aquí para colocar la firma en la sección del documento que la desees.',
			side: 'left' as const,
		},
	},
	{
		element: '[data-tour="annotation-page-thumbs"]',
		popover: {
			title: 'Páginas',
			description:
				'Toca aquí para ver las páginas del documento y saltar a cualquiera de ellas.',
			side: 'top' as const,
		},
	},
	{
		element: '[data-tour="annotation-send-btn"]',
		popover: {
			title: 'Envía a firmar el documento',
			description:
				'Cuando hayas colocado en el documento los campos a firmar, haz click aquí para enviar a firmar el documento.',
			side: 'bottom' as const,
		},
	},
];

const SignDocument = () => {
	const [componentState, setComponentState] = useState<
		'document-upload' | 'document-annotate'
	>('document-upload');
	const [documentToSign, setDocumentToSign] = useState<File | null>(null);
	const { isResponsive } = useWindowDimensions();

	useEffect(() => {
		if (!hasSeenTour('sign-document-upload')) {
			startTour('sign-document-upload', UPLOAD_TOUR_STEPS);
		}
	}, []);

	useEffect(() => {
		if (componentState !== 'document-annotate') return;
		if (hasSeenTour('sign-document-annotator')) return;
		const steps = isResponsive
			? ANNOTATOR_TOUR_STEPS_MOBILE
			: ANNOTATOR_TOUR_STEPS_DESKTOP;
		const timer = setTimeout(
			() => startTour('sign-document-annotator', steps),
			150,
		);
		return () => clearTimeout(timer);
		// isResponsive intentionally excluded: capture layout mode at
		// the moment the annotator first appears, not on every resize.
	}, [componentState]);

	let step;

	switch (componentState) {
		case 'document-upload':
			step = (
				<DocumentUpload
					onUploadDocument={() =>
						setComponentState('document-annotate')
					}
					setDocumentToSign={setDocumentToSign}
				/>
			);
			break;
		case 'document-annotate':
			step = (
				<DocumentAnnotator
					documentToSign={documentToSign}
					onDiscardDocument={() =>
						setComponentState('document-upload')
					}
				/>
			);
			break;
	}

	return (
		<div
			className={
				styles['sign-document-container'] + ' cyber-main with-sidenav'
			}
		>
			<div className="cyber-spacer section"></div>
			<div className={styles['sign-document']}>{step}</div>
		</div>
	);
};

export default SignDocument;
